import React, { useEffect } from 'react';
import { configService } from "../Config/ConfigService";
import Payment from './Payment';
import { AffirmConfig } from './AffirmModels';
import { affirmClient } from './AffirmClient';
import { EnvironmentConfiguration } from '../Config/EnvironmentConfiguration';
import { deepCopy } from '../../Components/Shared/Utility/DeepCopy';
import { PaymentType } from './PaymentType';
import Physician from '../../Components/Physician/Models/Physician';
import InvoiceModel from '../../Components/Shared/Models/InvoiceModel';
import { Button, Card } from 'react-bootstrap';
import isNullOrUndefined from '../../Components/Shared/Utility/isNullOrUndefined';

declare global {
    interface Window {
        affirm: any;
    }
}

interface IProps{
    onPaymentChanged: (payment: Payment) => void;
    onPaymentTypeChanged: () => void;
    onAffirmCheckoutComplete: () => void;
    physician: Physician;
    invoices: InvoiceModel[];
    payment: Payment;
    disabled?: boolean;

}

export const AffirmCheckout = (props: IProps) => {
    
    useEffect(() => {
        async function initialize() {
            await affirmClient.initializeAffirm();
            var config = await configService.getConfiguration();
            window.affirm.ui.ready(() => {
                var affirmConfig = getAffirmConfig(props.physician, props.invoices, props.payment, config);
                console.log(affirmConfig);
                window.affirm.checkout(affirmConfig);
            });
        }
        initialize();
    }, []);

    const startCheckout = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        window.affirm.checkout.open({
            onFail: loanCancelledOrFailed,
            onSuccess: loanConfirmedByUser,  
            onOpen: function(token: any){
               console.log("Affirm modal was opened successfully, checkout token is: " + token)},
            onValidationError: loanCancelledOrFailed
        });
    }

     const loanConfirmedByUser =  (response: any) => {
        console.log(response);
        const updated = deepCopy(props.payment);
        updated.paymentType = PaymentType.Affirm;
        updated.PaymentToken = response.checkout_token;
        updated.IsValid = true;
        props.onPaymentChanged(updated);
        props.onPaymentTypeChanged();
        props.onAffirmCheckoutComplete();
    }

    const loanCancelledOrFailed = () => {
        const updated = deepCopy(props.payment);
        updated.paymentType = null;
        updated.IsValid = false;
        props.onPaymentChanged(updated);
        props.onPaymentTypeChanged();
    }

    const getOrderId = (physician: Physician, primaryInvoice: InvoiceModel) : string => {
        return !(isNullOrUndefined(primaryInvoice) || isNullOrUndefined(primaryInvoice.invoiceNumber)) 
            ? [physician.physicianId, primaryInvoice.invoiceNumber].join(".")
            : physician.physicianId.toString();
    }

    const getAffirmConfig = (physician: Physician, invoices: InvoiceModel[], payment: Payment, config: EnvironmentConfiguration): AffirmConfig => {
        
        var contactAddress = physician.addresses.addresses.filter(_ => _.isContact)[0];
        var country = physician.addresses.countries.find(_ => _.id == contactAddress.countryId);
        var primaryInvoice = invoices.sort((a, b) => b.amountDue() - a.amountDue())[0];

        var items = [];
        for (var i = 0; i < primaryInvoice.lineItems.length; i++){
            items.push({
                display_name: primaryInvoice.lineItems[i].description,
                sku: primaryInvoice.lineItems[i].product.productCode,
                unit_price: primaryInvoice.lineItems[i].amount*100,
                qty: 1
            })
        }


        return {
            merchant: {
                user_confirmation_url: "https://abpn.com",
                user_cancel_url: "https://abpn.com",
                user_confirmation_url_action: "GET",
                public_api_key: config.Affirm.PublicApiKey,
                name: "The American Board of Psychiatry and Neurology"
            },
            shipping: {
                name: {
                    first: physician.firstName,
                    last: physician.lastName
                },
                address: {
                    line1: contactAddress.line1,
                    line2: contactAddress.line2,
                    city: contactAddress.city,
                    state: contactAddress.stateCode,
                    zipcode: contactAddress.zipCode,
                    country: country.isO3Code
                },
                phone_number: contactAddress.homePhoneNumber.replace(/\D/g,''),
                email: payment.email
            },
            billing:{
                name: {
                    first: physician.firstName,
                    last: physician.lastName
                },
                address: {
                    line1: contactAddress.line1,
                    line2: contactAddress.line2,
                    city: contactAddress.city,
                    state: contactAddress.stateCode,
                    zipcode: contactAddress.zipCode,
                    country: country.isO3Code
                },
                phone_number: contactAddress.homePhoneNumber.replace(/\D/g,''),
                email: payment.email
            },
            items:items,
            metadata: {
                mode: "modal"
            },
            order_id: getOrderId(physician, primaryInvoice),
            currency: "USD",
            shipping_amount: 0,
            tax_amount: 0,
            total: primaryInvoice.amountDue()*100  
        };
    }

    return (
        <React.Fragment>
            <div>
                <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller mt-3">
                    Click the button below to begin the checkout process to pay over time with Affirm.
                </Card.Text>
                <Button variant="pp-primary" disabled={props.disabled} onClick={startCheckout}>Checkout With Affirm</Button>
            </div>
        </React.Fragment>
    );
}

export default AffirmCheckout;